import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Tooltip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Restore as RestoreIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  OpenInNew as PreviewIcon
} from '@mui/icons-material';
import fileService, { FileVersion } from '../../services/fileService';
import { format } from 'date-fns';
import FilePreview from '../preview/FilePreview';
import config from '../../config';

interface VersionHistoryProps {
  fileId: string;
  onVersionRestored?: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const VersionHistory: React.FC<VersionHistoryProps> = ({ fileId, onVersionRestored }) => {
  const [versions, setVersions] = useState<FileVersion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [currentVersion, setCurrentVersion] = useState<FileVersion | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState<boolean>(false);
  const [previewVersionId, setPreviewVersionId] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setLoading(true);
        const response = await fileService.getFileVersions(fileId);
        setVersions(response.versions);
      } catch (err) {
        setError('Failed to load version history');
      } finally {
        setLoading(false);
      }
    };

    fetchVersions();
  }, [fileId]);

  const handleDownloadVersion = (versionId: string) => {
    window.open(`${config.server.baseUrl}/files/${fileId}/versions/${versionId}/download`, '_blank');
  };

  const handleRestore = async (versionId: string) => {
    try {
      setRestoring(true);
      await fileService.restoreFileVersion(fileId, versionId);
      if (onVersionRestored) {
        onVersionRestored();
      }
    } catch (err) {
      setError('Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async (versionId: string) => {
    try {
      await fileService.deleteFileVersion(fileId, versionId);
      setVersions(versions.filter(v => v.id !== versionId));
    } catch (err) {
      setError('Failed to delete version');
    }
  };

  const openRestoreConfirm = (version: FileVersion) => {
    setCurrentVersion(version);
    setConfirmDialogOpen(true);
  };

  const handlePreviewVersion = (versionId: string) => {
    setPreviewVersionId(versionId);
    setPreviewDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (versions.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          No version history available
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center' }}>
        <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">Version History</Typography>
      </Box>
      
      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        <List disablePadding>
          {versions.map((version, index) => (
            <React.Fragment key={version.id}>
              <ListItem
                secondaryAction={
                  <Box>
                    <Tooltip title="Download">
                      <IconButton 
                        edge="end" 
                        size="small" 
                        onClick={() => handleDownloadVersion(version.id)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!version.isCurrent && (
                      <>
                        <Tooltip title="Restore">
                          <IconButton 
                            edge="end" 
                            size="small" 
                            onClick={() => openRestoreConfirm(version)}
                            disabled={restoring}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            edge="end" 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(version.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                }
                sx={{ 
                  bgcolor: version.isCurrent ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2">
                        Version {version.version} 
                        {version.isCurrent && (
                          <Typography 
                            component="span" 
                            variant="caption" 
                            sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}
                          >
                            (Current)
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" component="span">
                        {formatDate(version.createdAt)}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                        {formatBytes(version.size)}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                        By {version.createdBy}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
              {index < versions.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Restore Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Restore Version</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to restore version {currentVersion?.version}? This will create a new version with the content from this older version.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => currentVersion && handleRestore(currentVersion.id)} 
            color="primary" 
            autoFocus
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Version Preview
          <IconButton
            aria-label="close"
            onClick={() => setPreviewDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            &times;
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '70vh', p: 0 }}>
          {previewVersionId && (
            <FilePreview
              fileId={fileId}
              versionId={previewVersionId}
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default VersionHistory; 